/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { SelectOption } from '@zencity/common-ui/lib/ZCD/ZCDFilter/types';
import { FilterPositions, IFilterDefinition } from '@zencity/reporting-types';
import { Option } from 'components/Dropdown/Dropdown';
import i18next from 'i18next';
import React from 'react';
import { BooleanFilter } from 'screens/WidgetGenerator/reportGeneratorComponents/components/GenericFilters/BooleanFilter';
import { NumericFilter } from 'screens/WidgetGenerator/reportGeneratorComponents/components/GenericFilters/NumericFilter';
import { CyclesFilter } from 'screens/WidgetGenerator/reportGeneratorComponents/components/GlobalFilters/CyclesFilter';
import { GenericQuestionSelectFilter } from 'screens/WidgetGenerator/reportGeneratorComponents/components/GlobalFilters/GenericQuestionSelectFilter/GenericQuestionSelectFilter';
import { SurveySelectFilter } from 'screens/WidgetGenerator/reportGeneratorComponents/components/QuestionnaireHOC/SurveySelectFilter';
import {
  FILTERS_LABELS,
  QUESTIONNAITE_DEFAULT_SPLIT_POSITION,
  ReportBuilderFilterProps,
} from 'screens/WidgetGenerator/reportGeneratorComponents/constants';
import { SurveysReportGeneratorFilterKey } from 'screens/WidgetGenerator/reportGeneratorComponents/enums';
import { ClassifiedGenericQuestionsFilter } from 'screens/WidgetGenerator/reportGeneratorComponents/filters/ClassifiedGenericQuestionsFilter';
import { GeographicDivisionsFilter } from 'screens/WidgetGenerator/reportGeneratorComponents/filters/GeographicDivisionsFilter/GeographicDivisionsFilter';
import { SurveyGroupFilter } from 'screens/WidgetGenerator/reportGeneratorComponents/filters/SurveyGroupFilter';
import { rakeWeighterExecutionLabelForFilter } from 'screens/WidgetGenerator/reportGeneratorComponents/helpers/cycles';
import { divisionLabelForFilter } from 'screens/WidgetGenerator/reportGeneratorComponents/helpers/division';
import { surveyGroupLabelForFilter } from 'screens/WidgetGenerator/reportGeneratorComponents/helpers/surveyGroup';
import { ReportBuilderClientData } from 'screens/WidgetGenerator/reportGeneratorComponents/types';

import { OverrideColorsMappingFilter } from 'screens/WidgetGenerator/reportGeneratorComponents/filters/OverrideColorsMappingFilter';
import { ToggleDivisionChildrenOrSiblingsFilter } from 'screens/WidgetGenerator/reportGeneratorComponents/filters/ToggleDivisionChildrenOrSiblingsFilter';
import { QuestionType } from 'types/genericQuestion';
import { AspectValue, MaintainAndFocusServicesWidgetData, OverallSatisfactionScoreQuestion } from '../utils/misc';
import { AspectFilter } from './components/BenchmarkGenericQuestionsTableHOC/AspectFilter';
import {
  CrossTabsFilters,
  CrossTabsFiltersSelectedData,
  CrossTabsFiltersType,
} from './components/CrossTabsHOC/CrossTabsFilters';
import { MaintainAndFocusFilters } from './components/MaintainAndFocusServicesHOC/MaintainAndFocusFilters';
import { AspectsFilter } from './components/QuestionnaireHOC/AspectsFilter';
import { SentimentFilter } from './components/TopicsBubbleChartHOC/SentimentFilter';
import { withWidgetGeneratorContextForFilters } from './components/WidgetWrappers/WidgetWrappers';
import { ReportAspectsFilter } from './filters/ReportAspectsFilter';

const maintainAndFocusFiltersDisplay = (value: MaintainAndFocusServicesWidgetData) => {
  const formatSelectedDataToString = (filterData: OverallSatisfactionScoreQuestion[]) =>
    filterData?.map((service) => service.text).join(', ');

  const filterDataToDisplay = {
    maintain: formatSelectedDataToString(value.maintain),
    focus: formatSelectedDataToString(value.focus),
  };

  return `${i18next.t('widgetGenerator.filters.maintainAndFocus.maintainServices')} ${
    filterDataToDisplay.maintain
  } ${i18next.t('widgetGenerator.filters.maintainAndFocus.focusServices')} ${filterDataToDisplay.focus}`;
};
const crossTabsFiltersDisplay = (selectedData: CrossTabsFiltersSelectedData) => {
  const formatSelectedDataToString = (filterData: SelectOption[]) =>
    filterData?.map((option) => option.label).join(', ');

  const filterDataToDisplay = {
    [CrossTabsFiltersType.BASE_QUESTION]: selectedData[CrossTabsFiltersType.BASE_QUESTION]?.label,
    [CrossTabsFiltersType.CROSS_QUESTIONS]: formatSelectedDataToString(
      selectedData[CrossTabsFiltersType.CROSS_QUESTIONS],
    ),
  };

  return `${i18next.t('widgetGenerator.filters.maintainAndFocus.maintainServices')} ${
    filterDataToDisplay[CrossTabsFiltersType.BASE_QUESTION]
  } ${i18next.t('widgetGenerator.filters.maintainAndFocus.focusServices')} ${
    filterDataToDisplay[CrossTabsFiltersType.CROSS_QUESTIONS]
  }`;
};

export const buildFilterDefinitions = (clientData: ReportBuilderClientData): IFilterDefinition[] => [
  {
    key: SurveysReportGeneratorFilterKey.SURVEY_GROUP,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.SURVEY_GROUP],
    isGlobal: true,
    position: FilterPositions.MAIN_BAR,
    getValueLabel: (value) => surveyGroupLabelForFilter(value, clientData),
    component: (props) => <SurveyGroupFilter {...props} clientData={clientData} />,
  },
  {
    key: SurveysReportGeneratorFilterKey.CLASSIFIED_OPEN_ENDED_GENERIC_QUESTION,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.CLASSIFIED_OPEN_ENDED_GENERIC_QUESTION],
    component: (props) => <ClassifiedGenericQuestionsFilter {...props} clientData={clientData} />,
  },
  {
    key: SurveysReportGeneratorFilterKey.CYCLES,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.CYCLES],
    isGlobal: true,
    position: FilterPositions.MAIN_BAR,
    component: (props) => <CyclesFilter {...props} clientData={clientData} />,
    getValueLabel: (value) => rakeWeighterExecutionLabelForFilter(value, clientData),
  },
  {
    key: SurveysReportGeneratorFilterKey.DIVISION,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.DIVISION],
    component: (props) => <GeographicDivisionsFilter {...props} clientData={clientData} />,
    getValueLabel: (value) => divisionLabelForFilter(value, clientData),
  },
  {
    key: SurveysReportGeneratorFilterKey.DIVISION_SIBLINGS_OR_CHILDREN_TOGGLE,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.DIVISION_SIBLINGS_OR_CHILDREN_TOGGLE],
    component: (props) => <ToggleDivisionChildrenOrSiblingsFilter {...props} clientData={clientData} />,
    getValueLabel: (value) =>
      value ? i18next.t('widgetGenerator.displayChildren') : i18next.t('widgetGenerator.displaySiblings'),
  },
  {
    key: SurveysReportGeneratorFilterKey.OVERRIDE_COLORS_MAPPING,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.OVERRIDE_COLORS_MAPPING],
    component: withWidgetGeneratorContextForFilters(OverrideColorsMappingFilter, clientData),
    getValueLabel: (value) => JSON.stringify(value),
  },
  {
    key: SurveysReportGeneratorFilterKey.SERVICES_BAR_CHART_ROW_TO_DISPLAY,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.SERVICES_BAR_CHART_ROW_TO_DISPLAY],
    component: (props: ReportBuilderFilterProps) =>
      NumericFilter({
        reportBuilderProps: props,
        title: i18next.t('widgetGenerator.widgets.servicesBarChart.rowToDisplayFilter.title'),
        placeholder: i18next.t('widgetGenerator.widgets.servicesBarChart.rowToDisplayFilter.placeholder'),
        minValue: 1,
      }),
  },
  {
    key: SurveysReportGeneratorFilterKey.SERVICES_BAR_CHART_NUM_BARS_PER_ROW,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.SERVICES_BAR_CHART_NUM_BARS_PER_ROW],
    component: (props: ReportBuilderFilterProps) =>
      NumericFilter({
        reportBuilderProps: props,
        title: i18next.t('widgetGenerator.widgets.servicesBarChart.numBarsPerTable.title'),
        placeholder: i18next.t('widgetGenerator.widgets.servicesBarChart.numBarsPerTable.placeholder'),
        minValue: 1,
      }),
  },
  {
    key: SurveysReportGeneratorFilterKey.SERVICES_BAR_CHART_TABLE_TO_DISPLAY,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.SERVICES_BAR_CHART_TABLE_TO_DISPLAY],
    component: (props: ReportBuilderFilterProps) =>
      NumericFilter({
        reportBuilderProps: props,
        title: i18next.t('widgetGenerator.filters.servicesBarChart.servicesBarChartTableToDisplay'),
        minValue: 1,
      }),
  },
  {
    key: SurveysReportGeneratorFilterKey.SELECTED_SERVICE_BAR_ASPECTS,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.SELECTED_SERVICE_BAR_ASPECTS],
    component: withWidgetGeneratorContextForFilters(ReportAspectsFilter, clientData),
  },
  {
    key: SurveysReportGeneratorFilterKey.TOPICS_BUBBLE_CHART_SCALE_SELECT,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.TOPICS_BUBBLE_CHART_SCALE_SELECT],
    component: (props: ReportBuilderFilterProps) =>
      NumericFilter({
        reportBuilderProps: props,
        title: i18next.t('widgetGenerator.filters.topicsBubbleChart.scaleFilter.title'),
        placeholder: i18next.t('widgetGenerator.filters.topicsBubbleChart.scaleFilter.placeholder'),
        minValue: 1,
        defaultValue: 3,
      }),
  },
  {
    key: SurveysReportGeneratorFilterKey.TOPICS_BUBBLE_CHART_SENTIMENT_SELECT,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.TOPICS_BUBBLE_CHART_SENTIMENT_SELECT],
    component: SentimentFilter,
    getValueLabel: (selectedSurveyOption: SelectOption) => selectedSurveyOption.label,
  },
  {
    key: SurveysReportGeneratorFilterKey.DEMOGRAPHICS_TABLE_ROWS_PER_TABLE,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.DEMOGRAPHICS_TABLE_ROWS_PER_TABLE],
    component: (props: ReportBuilderFilterProps) =>
      NumericFilter({
        reportBuilderProps: props,
        title: i18next.t('widgetGenerator.filters.demographics.rowsPerTable.name'),
        placeholder: i18next.t('widgetGenerator.filters.demographics.rowsPerTable.placeholder'),
        minValue: 1,
        defaultValue: 8,
      }),
  },
  {
    key: SurveysReportGeneratorFilterKey.WORD_CLOUD_GENERIC_QUESTION_SELECT,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.WORD_CLOUD_GENERIC_QUESTION_SELECT],
    component: (reportBuilderProps) =>
      GenericQuestionSelectFilter({
        reportBuilderProps: {
          ...reportBuilderProps,
          clientData,
        },
        questionTypesToInclude: [QuestionType.OPEN_ENDED],
      }),
    getValueLabel: (value: Option) => value.label,
  },
  {
    key: SurveysReportGeneratorFilterKey.WORD_CLOUD_SHOW_QUESTION_TEXT,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.WORD_CLOUD_SHOW_QUESTION_TEXT],
    component: (props: ReportBuilderFilterProps) =>
      BooleanFilter({
        reportBuilderProps: props,
        filterLabel: i18next.t('widgetGenerator.filters.wordCloud.showQuestionText'),
        defaultValue: true,
      }),
    getValueLabel: (value: boolean) => (value ? i18next.t('common.yes') : i18next.t('common.no')),
  },
  {
    key: SurveysReportGeneratorFilterKey.QUESTIONNAIRE_SURVEY_SINGLE_SELECT,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.QUESTIONNAIRE_SURVEY_SINGLE_SELECT],
    component: withWidgetGeneratorContextForFilters(SurveySelectFilter, clientData),
    getValueLabel: (selectedSurveyOption: SelectOption) => selectedSurveyOption.label,
  },
  {
    key: SurveysReportGeneratorFilterKey.QUESTIONNAIRE_ASPECTS_SELECT,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.QUESTIONNAIRE_ASPECTS_SELECT],
    component: AspectsFilter,
    getValueLabel: (selectedData: SelectOption[]) => selectedData?.map((option) => option.label).join(', '),
  },
  {
    key: SurveysReportGeneratorFilterKey.QUESTIONNAIRE_TABLE_SPLIT_POSITION,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.QUESTIONNAIRE_TABLE_SPLIT_POSITION],
    component: (props: ReportBuilderFilterProps) =>
      NumericFilter({
        reportBuilderProps: props,
        title: i18next.t('widgetGenerator.filters.questionnaire.selectSplitPosition'),
        placeholder: i18next.t('widgetGenerator.filters.questionnaire.selectSplitPosition'),
        minValue: 1,
        defaultValue: QUESTIONNAITE_DEFAULT_SPLIT_POSITION,
      }),
  },
  {
    key: SurveysReportGeneratorFilterKey.QUESTIONNAIRE_TABLE_TO_DISPLAY,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.QUESTIONNAIRE_TABLE_TO_DISPLAY],
    component: (props: ReportBuilderFilterProps) =>
      NumericFilter({
        reportBuilderProps: props,
        title: i18next.t('widgetGenerator.filters.questionnaire.selectTableToDisplay'),
        placeholder: i18next.t('widgetGenerator.filters.questionnaire.selectTableToDisplay'),
        minValue: 1,
      }),
  },
  {
    key: SurveysReportGeneratorFilterKey.MAINTAIN_AND_FOCUS_FILTERS,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.MAINTAIN_AND_FOCUS_FILTERS],
    component: withWidgetGeneratorContextForFilters(MaintainAndFocusFilters, clientData),
    getValueLabel: maintainAndFocusFiltersDisplay,
  },
  {
    key: SurveysReportGeneratorFilterKey.CROSS_TABS_FILTERS,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.CROSS_TABS_FILTERS],
    component: withWidgetGeneratorContextForFilters(CrossTabsFilters, clientData),
    getValueLabel: crossTabsFiltersDisplay,
  },
  {
    key: SurveysReportGeneratorFilterKey.SERVICE_SENTIMENT_BAR_QUESTION_SELECT,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.SERVICE_SENTIMENT_BAR_QUESTION_SELECT],
    component: (reportBuilderProps) =>
      GenericQuestionSelectFilter({
        reportBuilderProps: {
          ...reportBuilderProps,
          clientData,
        },
        aspect: AspectValue.CHARACTERISTIC,
        questionTypesToInclude: [QuestionType.SCALE],
      }),
    getValueLabel: (value: Option) => value.label,
  },
  {
    key: SurveysReportGeneratorFilterKey.CHOICES_QUESTION_CHART_QUESTION_SELECT,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.CHOICES_QUESTION_CHART_QUESTION_SELECT],
    component: (reportBuilderProps) =>
      GenericQuestionSelectFilter({
        reportBuilderProps: {
          ...reportBuilderProps,
          clientData,
        },
        questionTypesToInclude: [QuestionType.CHOICES],
      }),
    getValueLabel: (value: Option) => value.label,
  },
  {
    key: SurveysReportGeneratorFilterKey.CHOICES_QUESTION_CHART_DISPLAY_TYPE,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.CHOICES_QUESTION_CHART_DISPLAY_TYPE],
    component: (props: ReportBuilderFilterProps) =>
      BooleanFilter({
        reportBuilderProps: props,
        filterLabel: i18next.t('widgetGenerator.filters.choicesQuestionChart.showPercentageResults'),
        defaultValue: true,
      }),
    getValueLabel: (value: boolean) => (value ? i18next.t('common.yes') : i18next.t('common.no')),
  },
  {
    key: SurveysReportGeneratorFilterKey.CHOICES_QUESTION_CHART_ORDER_BY,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.CHOICES_QUESTION_CHART_ORDER_BY],
    component: (props: ReportBuilderFilterProps) =>
      BooleanFilter({
        reportBuilderProps: props,
        filterLabel: i18next.t('widgetGenerator.filters.choicesQuestionChart.orderByPopularity'),
        defaultValue: false,
      }),
    getValueLabel: (value: boolean) => (value ? i18next.t('common.yes') : i18next.t('common.no')),
  },
  {
    key: SurveysReportGeneratorFilterKey.BENCHMARK_ASPECT_SELECT,
    name: FILTERS_LABELS[SurveysReportGeneratorFilterKey.BENCHMARK_ASPECT_SELECT],
    component: AspectFilter,
    getValueLabel: (selectedData: SelectOption) => selectedData.label,
  },
];
